import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { CheckCircle, XCircle, Loader, Clock } from "lucide-react";

// Landing page for the toyyibPay return URL:
// /payment-status?status_id=1&billcode=xxx&order_id=yyy
export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | success | pending | failed
  const [total, setTotal] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const verified = useRef(false);

  const billcode = searchParams.get("billcode");
  const statusId = searchParams.get("status_id");

  useEffect(() => {
    if (verified.current) return;
    verified.current = true;

    if (!billcode) {
      setState("failed");
      return;
    }

    // Always hit verify so we can settle success or discard clear failures.
    // Pass status_id so the API only discards when toyyibPay reports failure.
    const qs = statusId ? `?status_id=${encodeURIComponent(statusId)}` : "";
    apiGet(`/payments/toyyibpay/verify/${billcode}${qs}`)
      .then((r) => {
        if (r.data.paid) {
          setTotal(r.data.total);
          setState("success");
        } else if (r.data.pending) {
          setState("pending");
        } else {
          setState("failed");
        }
      })
      .catch(() => setState(statusId === "3" ? "failed" : "failed"));
  }, [billcode, statusId]);

  useEffect(() => {
    if (state !== "success") return;
    if (countdown <= 0) {
      navigate("/my-payments");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [state, countdown, navigate]);

  return (
    <div className="section-panel" data-testid="payment-status-panel">
      <div style={{ padding: "48px 24px", textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
        {state === "verifying" && (
          <>
            <Loader size={48} color="var(--brand)" style={{ animation: "spin 1s linear infinite" }} />
            <h2 style={{ margin: "16px 0 8px" }}>Verifying your payment...</h2>
            <p style={{ color: "var(--text2)", fontSize: 13 }}>Please wait while we confirm your payment with toyyibPay.</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle size={56} color="var(--success)" />
            <h2 style={{ margin: "16px 0 8px", color: "var(--success)" }} data-testid="payment-success-message">
              Thank you for your payment RM {Number(total).toFixed(2)}
            </h2>
            <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
              Your payment has been recorded successfully.
              Redirecting to My Payment in {countdown}s...
            </p>
            <button className="btn btn-primary" onClick={() => navigate("/my-payments")} data-testid="goto-my-payments-btn">
              View My Payments
            </button>
          </>
        )}

        {state === "pending" && (
          <>
            <Clock size={56} color="var(--warning, #d97706)" />
            <h2 style={{ margin: "16px 0 8px" }}>Payment is being processed</h2>
            <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
              Your payment is still pending confirmation from the bank. It will appear in My Payment once confirmed.
            </p>
            <button className="btn btn-primary" onClick={() => navigate("/my-payments")}>
              View My Payments
            </button>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle size={56} color="var(--danger)" />
            <h2 style={{ margin: "16px 0 8px", color: "var(--danger)" }}>Payment was not completed</h2>
            <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
              The payment failed or was cancelled. No charges were made — you may try again.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn btn-primary" onClick={() => navigate("/submit-payment")}>
                Try Again
              </button>
              <button className="btn" onClick={() => navigate("/my-payments")}>
                View My Payments
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
